import React, { Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Mail, Github, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useTypingEffect } from "../hooks/useHome";

const Skills = React.lazy(() => import("./Skills"));

const stack = ["Go", "Python", "Kafka", "Docker", "REST APIs", "Linux", "Distributed Systems"];

function Home() {
    const command = useTypingEffect("whoami", 110);
    const done = command === "whoami";
    const [showSkills, setShowSkills] = useState(false);

    useEffect(() => {
        const revealSkills = () => setShowSkills(true);
        if ("requestIdleCallback" in window) {
            const id = window.requestIdleCallback(revealSkills, { timeout: 1500 });
            return () => window.cancelIdleCallback(id);
        }
        const timeout = window.setTimeout(revealSkills, 1200);
        return () => window.clearTimeout(timeout);
    }, []);

    return (
        <div className="mx-auto max-w-5xl px-6">
            <section className="flex min-h-[calc(100vh-var(--nav-height)-4rem)] flex-col justify-center py-16">
                <p className="mb-6 font-mono text-sm text-muted-foreground">
                    <span className="text-primary">mauricio@denver</span>:~$ {command}
                    <span className="ml-0.5 inline-block h-4 w-2 translate-y-0.5 animate-pulse bg-primary" aria-hidden="true" />
                </p>

                <div
                    className={`transition-opacity duration-700 ${done ? "opacity-100" : "opacity-0"}`}
                    aria-hidden={!done}
                >
                    <h1 className="max-w-3xl text-4xl font-bold tracking-tight sm:text-6xl">
                        Mauricio Gonzalez
                    </h1>
                    <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
                        Backend engineer at bet365. I build Go microservices and APIs for
                        high-volume trading systems. Before that I worked enterprise networking at
                        Cisco Meraki. I studied computer science at Colorado State University, with a
                        concentration in networks and security.
                    </p>

                    <div className="mt-6 flex flex-wrap gap-2">
                        {stack.map((item) => (
                            <Badge key={item} variant="secondary" className="font-mono font-normal">
                                {item}
                            </Badge>
                        ))}
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <Button asChild>
                            <Link to="/projects">
                                View projects <ArrowRight />
                            </Link>
                        </Button>
                        <Button variant="outline" size="icon" asChild>
                            <a
                                href="https://www.linkedin.com/in/mauric10"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="LinkedIn profile"
                            >
                                <Linkedin />
                            </a>
                        </Button>
                        <Button variant="outline" size="icon" asChild>
                            <a href="mailto:mauricio@gonzalez-medina.com" aria-label="Send me an email">
                                <Mail />
                            </a>
                        </Button>
                        <Button variant="outline" size="icon" asChild>
                            <a
                                href="https://github.com/mauriciog88u1"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="GitHub profile"
                            >
                                <Github />
                            </a>
                        </Button>
                    </div>

                    <p className="mt-10 hidden font-mono text-xs text-muted-foreground sm:block">
                        tip: press <kbd className="rounded border bg-muted px-1.5 py-0.5">⌘K</kbd> to open the terminal and look around
                    </p>
                    <p className="mt-10 font-mono text-xs text-muted-foreground sm:hidden">
                        tip: tap <span className="rounded border bg-muted px-1.5 py-0.5">{">_"}</span> above to open the terminal
                    </p>
                </div>
            </section>

            <section className="pb-8">
                <p className="font-mono text-sm text-muted-foreground">
                    <span className="text-primary">mauricio@denver</span>:~$ ls ~/skills
                </p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight">What I work with</h2>
                <div className="mt-6">
                    {showSkills ? (
                        <Suspense fallback={<p className="py-8 text-sm text-muted-foreground">Loading skills…</p>}>
                            <Skills />
                        </Suspense>
                    ) : (
                        <p className="py-8 text-sm text-muted-foreground">Loading skills…</p>
                    )}
                </div>
            </section>
        </div>
    );
}

export default Home;
